import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, useInView } from 'framer-motion';
import { cn } from '@/lib/utils';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// =========================================
// GSAP CHARACTER-BY-CHARACTER TEXT REVEAL
// =========================================
export const GSAPTextReveal = ({ 
  text, 
  className,
  delay = 0,
  staggerDelay = 0.03,
  splitBy = 'chars' // 'chars', 'words', 'lines'
}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    if (!textRef.current) return;

    const ctx = gsap.context(() => {
      // Split text into spans
      const element = textRef.current;
      const originalText = text;
      let spans = [];

      if (splitBy === 'chars') {
        element.innerHTML = originalText.split('').map((char, i) => 
          char === ' ' ? ' ' : `<span class="char-reveal inline-block" style="transform: translateY(100%); opacity: 0;">${char}</span>`
        ).join('');
        spans = element.querySelectorAll('.char-reveal');
      } else if (splitBy === 'words') {
        element.innerHTML = originalText.split(' ').map(word => 
          `<span class="word-reveal inline-block mr-2" style="transform: translateY(100%); opacity: 0;">${word}</span>`
        ).join('');
        spans = element.querySelectorAll('.word-reveal');
      }

      // Animate
      gsap.to(spans, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: staggerDelay,
        delay: delay,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, [text, delay, staggerDelay, splitBy]);

  return (
    <div ref={containerRef} className={cn('overflow-hidden', className)}>
      <span ref={textRef} className="inline-block">{text}</span>
    </div>
  );
};

// =========================================
// HORIZONTAL SCROLL PIN SECTION
// =========================================
export const HorizontalScrollSection = ({ children, className }) => {
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const scrollContainer = scrollContainerRef.current;
      const container = containerRef.current;
      
      if (!scrollContainer || !container) return;

      const scrollWidth = scrollContainer.scrollWidth;
      const containerWidth = container.offsetWidth;

      gsap.to(scrollContainer, {
        x: -(scrollWidth - containerWidth),
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          start: 'top top',
          end: `+=${scrollWidth - containerWidth}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className={cn('relative overflow-hidden', className)}>
      <div ref={scrollContainerRef} className="flex gap-8 will-change-transform">
        {children}
      </div>
    </div>
  );
};

// =========================================
// SCROLL-TRIGGERED SCALE REVEAL
// =========================================
export const ScrollScaleReveal = ({ children, className, scale = 0.8, delay = 0 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { scale: scale, opacity: 0, y: 60 },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 1,
          delay,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: ref.current,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        }
      );
    }, ref);

    return () => ctx.revert();
  }, [scale, delay]);

  return (
    <div ref={ref} className={cn('will-change-transform', className)}>
      {children}
    </div>
  );
};

// =========================================
// PARALLAX FLOATING ELEMENTS
// =========================================
export const ParallaxElement = ({ children, className, speed = 0.5, direction = 'y' }) => {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const movement = direction === 'y' ? { y: speed * 100 } : { x: speed * 100 };
      
      gsap.to(ref.current, {
        ...movement,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    }, ref);

    return () => ctx.revert();
  }, [speed, direction]);

  return (
    <div ref={ref} className={cn('will-change-transform', className)}>
      {children}
    </div>
  );
};

// =========================================
// NEON GLOW LINE ANIMATION
// =========================================
export const NeonGlowLine = ({ className }) => {
  const pathRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        pathRef.current,
        { strokeDashoffset: 1000, opacity: 0 },
        {
          strokeDashoffset: 0,
          opacity: 1,
          duration: 2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: pathRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );
    }, pathRef);

    return () => ctx.revert();
  }, []);

  return (
    <svg 
      viewBox="0 0 1200 200" 
      className={cn('w-full h-auto', className)}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="neonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(174, 100%, 27%)" stopOpacity="0" />
          <stop offset="50%" stopColor="hsl(174, 100%, 45%)" stopOpacity="1" />
          <stop offset="100%" stopColor="hsl(174, 100%, 27%)" stopOpacity="0" />
        </linearGradient>
        <filter id="neonBlur">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
        </filter>
      </defs>
      {/* Glow layer */}
      <path
        d="M 0 150 Q 300 50, 600 100 T 1200 80"
        fill="none"
        stroke="url(#neonGradient)"
        strokeWidth="8"
        filter="url(#neonBlur)"
        opacity="0.6"
      />
      {/* Main line */}
      <path
        ref={pathRef}
        d="M 0 150 Q 300 50, 600 100 T 1200 80"
        fill="none"
        stroke="url(#neonGradient)"
        strokeWidth="3"
        strokeDasharray="1000"
        strokeDashoffset="1000"
      />
    </svg>
  );
};

// =========================================
// LOADING TEXT ANIMATION (Like Student Tribe)
// =========================================
export const LoadingTextReveal = ({ text, className }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        textRef.current,
        { 
          opacity: 0.1,
          scale: 0.9,
          filter: 'blur(10px)',
        },
        {
          opacity: 1,
          scale: 1,
          filter: 'blur(0px)',
          duration: 1.5,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 70%',
            end: 'center center',
            scrub: 1,
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className={cn('overflow-hidden', className)}>
      <span 
        ref={textRef} 
        className="block text-center will-change-transform"
        style={{ textShadow: '0 0 40px hsl(174, 100%, 27%)' }}
      >
        {text}
      </span>
    </div>
  );
};

// =========================================
// STAGGERED CARDS REVEAL
// =========================================
export const StaggeredCardsContainer = ({ children, className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = containerRef.current.children;
      
      gsap.fromTo(
        cards,
        { 
          y: 80, 
          opacity: 0, 
          scale: 0.9,
          rotateX: 15 
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          rotateX: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className={cn('', className)} style={{ perspective: '1000px' }}>
      {children}
    </div>
  );
};

// =========================================
// MAGNETIC BUTTON (Like Cappen)
// =========================================
export const MagneticButton = ({ children, className, strength = 30 }) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(button, {
        x: x * (strength / 100),
        y: y * (strength / 100),
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(button, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'elastic.out(1, 0.3)',
      });
    };

    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [strength]);

  return (
    <div ref={buttonRef} className={cn('inline-block will-change-transform', className)}>
      {children}
    </div>
  );
};

// =========================================
// SCROLL-TRIGGERED NUMBER COUNTER
// =========================================
export const GSAPCounter = ({ 
  value, 
  suffix = '', 
  prefix = '',
  duration = 2,
  className 
}) => {
  const ref = useRef(null);
  const numberRef = useRef({ value: 0 });

  useEffect(() => {
    const ctx = gsap.context(() => {
      const numericValue = parseFloat(value.replace(/[^\d.]/g, ''));
      
      gsap.to(numberRef.current, {
        value: numericValue,
        duration: duration,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
        onUpdate: () => {
          if (ref.current) {
            const formatted = Math.round(numberRef.current.value).toLocaleString();
            ref.current.textContent = `${prefix}${formatted}${suffix}`;
          }
        },
      });
    }, ref);

    return () => ctx.revert();
  }, [value, suffix, prefix, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}0{suffix}
    </span>
  );
};

// =========================================
// TEXT SCRAMBLE EFFECT (Matrix Style)
// =========================================
export const TextScrambleGSAP = ({ text, className, trigger = 'hover' }) => {
  const ref = useRef(null);
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&';

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const scramble = () => {
      let iteration = 0;
      const originalText = text;

      const interval = setInterval(() => {
        element.textContent = originalText
          .split('')
          .map((char, index) => {
            if (index < iteration) {
              return originalText[index];
            }
            if (char === ' ') return ' ';
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join('');

        if (iteration >= originalText.length) {
          clearInterval(interval);
        }

        iteration += 1 / 3;
      }, 30);
    };

    if (trigger === 'scroll') {
      ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        onEnter: scramble,
        once: true,
      });
    } else if (trigger === 'hover') {
      element.addEventListener('mouseenter', scramble);
      return () => element.removeEventListener('mouseenter', scramble);
    }
  }, [text, trigger]);

  return (
    <span ref={ref} className={cn('font-mono', className)}>
      {text}
    </span>
  );
};

// =========================================
// FLOATING IMAGES PARALLAX (Like Student Tribe)
// =========================================
export const FloatingImagesBackground = ({ images, className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const floatingImages = containerRef.current.querySelectorAll('.floating-img');
      
      floatingImages.forEach((img, i) => {
        const speed = 0.1 + (Math.random() * 0.3);
        const direction = i % 2 === 0 ? 1 : -1;
        
        gsap.to(img, {
          y: direction * speed * 100,
          rotation: direction * 5,
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });

        // Subtle continuous float animation
        gsap.to(img, {
          y: `+=${direction * 10}`,
          duration: 3 + Math.random() * 2,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className={cn('absolute inset-0 overflow-hidden', className)}>
      {images.map((src, i) => (
        <div
          key={i}
          className="floating-img absolute rounded-lg opacity-20 will-change-transform"
          style={{
            left: `${10 + (i * 15) % 80}%`,
            top: `${5 + (i * 20) % 90}%`,
            width: `${80 + Math.random() * 60}px`,
            height: `${80 + Math.random() * 60}px`,
            transform: `rotate(${Math.random() * 20 - 10}deg)`,
          }}
        >
          <img 
            src={src} 
            alt="" 
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
      ))}
    </div>
  );
};

// =========================================
// MASK REVEAL ANIMATION
// =========================================
export const MaskReveal = ({ children, className, direction = 'up' }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const yStart = direction === 'up' ? 100 : direction === 'down' ? -100 : 0;
      const xStart = direction === 'left' ? 100 : direction === 'right' ? -100 : 0;

      gsap.fromTo(
        contentRef.current,
        { y: `${yStart}%`, x: `${xStart}%` },
        {
          y: '0%',
          x: '0%',
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, [direction]);

  return (
    <div ref={containerRef} className={cn('overflow-hidden', className)}>
      <div ref={contentRef} className="will-change-transform">
        {children}
      </div>
    </div>
  );
};

// =========================================
// SPLIT COLOR TEXT
// =========================================
export const SplitColorText = ({ 
  text, 
  splitAt, 
  firstColor = 'text-foreground', 
  secondColor = 'text-teal',
  className 
}) => {
  const firstPart = text.slice(0, splitAt);
  const secondPart = text.slice(splitAt);

  return (
    <span className={className}>
      <span className={firstColor}>{firstPart}</span>
      <span className={secondColor}>{secondPart}</span>
    </span>
  );
};

export default {
  GSAPTextReveal,
  HorizontalScrollSection,
  ScrollScaleReveal,
  ParallaxElement,
  NeonGlowLine,
  LoadingTextReveal,
  StaggeredCardsContainer,
  MagneticButton,
  GSAPCounter,
  TextScrambleGSAP,
  FloatingImagesBackground,
  MaskReveal,
  SplitColorText,
};
